/**
 * SupplierSelect - Dropdown component for selecting suppliers
 * Supports Bubakii and registered partner payees.
 */

import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { getPartners } from "../../features/partners/services/partnerService.js";
import { logError } from "../utils/logger.js";

/**
 * SupplierSelect Component
 * @param {Object} props
 * @param {string} props.value - Selected supplier value ("bubakii" or partner.id)
 * @param {Function} props.onChange - Callback when supplier is selected (receives supplier value)
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.required] - Whether field is required
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.defaultSupplier] - Default supplier from product (for auto-population)
 */
const SupplierSelect = ({
  value,
  onChange,
  label = "Supplier",
  disabled = false,
  required = false,
  error = null,
  defaultSupplier = null,
  ...otherProps
}) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Partners are the payable supplier entity. Drivers remain operational resources.
  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      try {
        const result = await getPartners({ isActive: true });
        if (result.success) {
          setPartners(result.data || []);
        }
      } catch (err) {
        logError("Error fetching partners", {
          error: err.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  // Auto-populate supplier from defaultSupplier prop if value is empty
  useEffect(() => {
    if (defaultSupplier && !value && onChange) {
      onChange(defaultSupplier);
    }
  }, [defaultSupplier, value, onChange]);

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <FormControl
      fullWidth
      required={required}
      error={!!error}
      disabled={disabled || loading}
      {...otherProps}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || "bubakii"}
        onChange={handleChange}
        label={label}
        disabled={disabled || loading}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1300, // Ensure dropdown appears above other elements
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          disableScrollLock: true, // Prevent scroll lock which can interfere with clicks
        }}
      >
        {/* Always render "bubakii" option - it doesn't need to be loaded */}
        <MenuItem value="bubakii">
          <Box>
            <Typography variant="body2" fontWeight="medium">
              Bubakii
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Internal supplier
            </Typography>
          </Box>
        </MenuItem>
        {value &&
          value !== "bubakii" &&
          !partners.some((partner) => partner.id === value) && (
          <MenuItem value={value}>
            <Box>
              <Typography variant="body2">{value}</Typography>
              <Typography variant="caption" color="warning.main">
                Existing supplier — select an active partner if this changed
              </Typography>
            </Box>
          </MenuItem>
        )}
        {/* Show loading state for partners */}
        {loading ? (
          <MenuItem disabled>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading partners...</Typography>
            </Box>
          </MenuItem>
        ) : (
          partners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {partner.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {partner.contactPerson || partner.id}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
          {error}
        </Typography>
      )}
    </FormControl>
  );
};

export default SupplierSelect;






